// GrowthBook bucketing identity: the hashing id the lander evaluates flags with,
// plus the geo country the geo-targeted flags are resolved against.
//
// Both live here because two consumers need them:
//   * App.jsx — sets them as GrowthBook attributes (`id`/`deviceId` and
//     `query`/`countryCode`, mirroring the platform's growthbook.service).
//   * utils/api.js — forwards them on the popup_first registration body so the
//     backend seeds the same experiment arms it will later BILL on (see
//     gbBucketingFields there).

const SWIPEY_BASE_URL = process.env.REACT_APP_SWIPEY_BASE_URL

// Persisted GrowthBook hashing id. A fresh Math.random() id per page load would
// re-bucket the visitor into a NEW variant on every load — including the Google
// OAuth return (a fresh load) — so the variant sent at registration would not match
// the one the returning popup shows. localStorage survives the same-origin OAuth
// return, so ONE identity holds for the whole funnel.
const GB_DEVICE_ID_KEY = 'gb_device_id'

export const getGbDeviceId = () => {
  const rand = () => Math.random().toString(36).substring(2)
  if (typeof window === 'undefined') return rand()
  try {
    let id = localStorage.getItem(GB_DEVICE_ID_KEY)
    if (!id) {
      id = rand()
      localStorage.setItem(GB_DEVICE_ID_KEY, id)
    }
    return id
  } catch (e) {
    return rand()
  }
}

// Geo country for GB targeting. sessionStorage (per-tab, like the nav-config and
// premium-token stickiness) so the value survives the SPA navigation + the Google
// OAuth round-trip without a second lookup.
const GB_COUNTRY_CODE_KEY = 'csfw_gb_country_code'

let resolvedCountryCode = ''

export const getGbCountryCode = () => {
  if (resolvedCountryCode) return resolvedCountryCode
  if (typeof window === 'undefined') return ''
  try {
    resolvedCountryCode = sessionStorage.getItem(GB_COUNTRY_CODE_KEY) || ''
  } catch (e) {
    // sessionStorage disabled (incognito / blocked) — in-memory value still works
  }
  return resolvedCountryCode
}

const setGbCountryCode = countryCode => {
  if (!countryCode) return
  resolvedCountryCode = countryCode
  try {
    sessionStorage.setItem(GB_COUNTRY_CODE_KEY, countryCode)
  } catch (e) {
    // ignore
  }
}

// The geo attributes GrowthBook is evaluated with. `query` carries the country code
// alongside `countryCode` because that is exactly what the platform passes
// (growthbook.service: `query: cookies['query'], countryCode`) — the lander has to
// mirror it or a rule targeting either attribute buckets the two differently.
// Empty object when unresolved, so it can be spread without stamping ''.
export const getGbGeoAttributes = () => {
  const countryCode = getGbCountryCode()
  return countryCode ? { query: countryCode, countryCode } : {}
}

let countryPromise = null

// Resolve the visitor's country from the backend (which reads it off the edge-set
// `x-country-code` header) and cache it for the tab. Memoized — every caller shares
// the one in-flight request.
//
// Deliberately NOT awaited before growthbook.init(): that would stall every other
// flag behind a network round-trip. setAttributes re-evaluates and the React SDK
// re-renders consumers, and checkout happens long after page load, so the value is
// settled well before any price is printed. A failed lookup leaves the geo
// attributes unset — the behaviour from before this existed.
export const ensureGbCountryCode = () => {
  if (countryPromise) return countryPromise
  const cached = getGbCountryCode()
  if (cached) {
    countryPromise = Promise.resolve(cached)
    return countryPromise
  }

  countryPromise = (async () => {
    try {
      const res = await fetch(`${SWIPEY_BASE_URL}/api/v1/auth/country-code`)
      if (!res.ok) return ''
      const data = await res.json()
      const countryCode = data?.countryCode || ''
      if (countryCode) setGbCountryCode(countryCode)
      return countryCode
    } catch (e) {
      console.warn('[growthbook] country-code lookup failed:', e)
      return ''
    }
  })()

  return countryPromise
}
